/**
 * Product page behaviour: the image slider, and the variant picker that drives
 * the add-to-cart form.
 *
 * Both are plain custom elements with no dependencies. If this script fails to
 * load the page still works — the slider is a native scroll container so it
 * stays swipeable, and the form's hidden variant input carries whatever Liquid
 * rendered, so it submits the first available variant rather than nothing.
 */

/**
 * The main slider, its arrows, its counter and the filmstrip beneath it.
 *
 * Position is read from `scrollLeft` rather than tracked in a variable, so a
 * swipe, an arrow press and a thumbnail click all arrive at the same answer and
 * there is no state to fall out of sync.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.filmstrip = this.querySelector('[data-gallery-thumbs]');
    this.current = this.querySelector('[data-gallery-current]');

    if (!this.viewport || this.slides.length === 0) return;

    this.ticking = false;

    this.querySelectorAll('[data-gallery-arrow]').forEach((button) => {
      button.addEventListener('click', () => this.goTo(this.index + Number(button.dataset.galleryArrow)));
    });

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goTo(index));
    });

    this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true });
  }

  /** Which slide is closest to the viewport's leading edge. */
  get index() {
    const width = this.viewport.clientWidth;
    if (!width) return 0;
    return Math.round(this.viewport.scrollLeft / width);
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    this.viewport.scrollTo({ left: clamped * this.viewport.clientWidth, behavior: 'smooth' });
  }

  /** Used by the variant picker when a colour brings its own photograph. */
  show(imageId) {
    const index = this.slides.findIndex((slide) => slide.dataset.imageId === String(imageId));
    if (index !== -1) this.goTo(index);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;

    requestAnimationFrame(() => {
      this.sync(this.index);
      this.ticking = false;
    });
  }

  sync(index) {
    if (this.current) this.current.textContent = String(index + 1).padStart(2, '0');

    this.thumbs.forEach((thumb, position) => {
      if (position === index) {
        thumb.setAttribute('aria-current', 'true');
        this.reveal(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  /** Keeps the active thumbnail inside the filmstrip's visible run. */
  reveal(thumb) {
    if (!this.filmstrip) return;

    const strip = this.filmstrip.getBoundingClientRect();
    const box = thumb.getBoundingClientRect();

    if (box.left < strip.left) {
      this.filmstrip.scrollBy({ left: box.left - strip.left, behavior: 'smooth' });
    } else if (box.right > strip.right) {
      this.filmstrip.scrollBy({ left: box.right - strip.right, behavior: 'smooth' });
    }
  }

}

/**
 * Turns the option controls into a variant.
 *
 * The selected values are read in DOM order, which is option order, and matched
 * against each variant's own `options` array. That is the whole lookup — it
 * never needs to know what the options are called or how many there are, so
 * adding a third one in the admin needs no change here.
 */
class ProductPurchase extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-variant-data]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch (error) {
      return;
    }

    this.form = this.querySelector('form');
    this.idInput = this.querySelector('[data-variant-id]');
    this.priceEl = this.querySelector('[data-price]');
    this.compareRow = this.querySelector('[data-compare-row]');
    this.compareEl = this.querySelector('[data-compare-price]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.quantity = this.querySelector('[name="quantity"]');
    this.giftInput = this.querySelector('[data-gift-input]');
    this.errorEl = this.querySelector('[data-form-error]');
    this.gallery = document.querySelector('product-gallery');

    if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

    this.querySelectorAll('[data-option-input]').forEach((input) => {
      if (input.type === 'radio') input.addEventListener('change', () => this.update());
    });

    this.querySelectorAll('[data-quantity-step]').forEach((button) => {
      button.addEventListener('click', () => this.step(Number(button.dataset.quantityStep)));
    });

    if (this.quantity) this.quantity.addEventListener('change', () => this.syncQuantity());

    this.syncQuantity();
  }

  get selection() {
    return Array.from(this.querySelectorAll('input[type="radio"][data-option-input]:checked')).map(
      (input) => input.value
    );
  }

  update() {
    const selection = this.selection;
    const variant = this.variants.find(
      (candidate) =>
        candidate.options.length === selection.length &&
        candidate.options.every((option, index) => option === selection[index])
    );

    // A combination the shop never created. Leave the price alone and block the
    // submit rather than silently ordering something else.
    if (!variant) {
      this.setAvailability(false, '選択できない組み合わせです');
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;
    if (this.priceEl) this.priceEl.textContent = variant.price;

    if (this.compareRow) {
      this.compareRow.hidden = !variant.hasOffer;
      if (variant.hasOffer && this.compareEl) this.compareEl.textContent = variant.compareAtPrice;
    }

    this.setAvailability(variant.available, variant.available ? '先行予約する' : '売り切れ');

    if (this.gallery && variant.imageId) this.gallery.show(variant.imageId);
  }

  setAvailability(available, label) {
    if (this.addButton) this.addButton.disabled = !available;
    if (this.addLabel) this.addLabel.textContent = label;
  }

  /**
   * Gift wrapping is a second product, and a form can only post one variant.
   *
   * When the box is ticked the submit is taken over and both lines are sent in
   * a single Ajax request; when it is not, nothing happens here and the browser
   * posts the form exactly as it always did. That way the ordinary purchase —
   * the overwhelming majority — never depends on this code path at all.
   */
  async onSubmit(event) {
    const giftId = this.giftInput && this.giftInput.checked && this.giftInput.dataset.variantId;
    if (!giftId || !this.idInput) return;

    event.preventDefault();
    this.showError('');
    this.setBusy(true);

    const quantity = Math.max(1, parseInt(this.quantity && this.quantity.value, 10) || 1);

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            // The property is what tells the packer which line to wrap.
            { id: Number(this.idInput.value), quantity, properties: { ギフト包装: '希望する' } },
            // One wrap per jacket.
            { id: Number(giftId), quantity },
          ],
        }),
      });

      const body = await response.json();
      if (!response.ok) throw new Error(body.description || body.message);

      window.location.href = this.dataset.cartUrl;
    } catch (error) {
      // Deliberately not falling back to a plain post: that would put the
      // jacket in the cart without the wrapping the customer asked and would be
      // charged for, which is worse than stopping and saying so.
      this.setBusy(false);
      this.showError('カートに追加できませんでした。時間をおいて、もう一度お試しください。');
    }
  }

  setBusy(busy) {
    if (!this.addButton || !this.addLabel) return;

    if (busy) {
      this.idleLabel = this.addLabel.textContent;
      this.addLabel.textContent = '処理中…';
    } else if (this.idleLabel) {
      this.addLabel.textContent = this.idleLabel;
    }

    this.addButton.disabled = busy;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  step(delta) {
    if (!this.quantity) return;

    const next = Math.max(1, (parseInt(this.quantity.value, 10) || 1) + delta);
    if (next === Number(this.quantity.value)) return;

    this.quantity.value = next;
    this.syncQuantity();
  }

  /** At one, there is nowhere down to go — the minus reads as unavailable. */
  syncQuantity() {
    const down = this.querySelector('[data-quantity-step="-1"]');
    if (down && this.quantity) down.disabled = (parseInt(this.quantity.value, 10) || 1) <= 1;
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('product-purchase')) customElements.define('product-purchase', ProductPurchase);
